import { useCallback, useEffect, useRef, useState } from "react";

import QRCodeStyling from "qr-code-styling";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
  SelectTrigger,
} from "@/shared/ui/select";
import { Modal } from "@/shared/ui/Modal";
import { Button } from "@/shared/ui/button";
import { Spinner } from "@/shared/ui/spinner";
import { Skeleton } from "@/shared/ui/skeleton";
import { useToast } from "@/shared/hooks/useToast";
import { LeadLinkTarget } from "@/entities/lead-link";

// REVIEW: вынеси типы в отдельный файл /links-page/model
enum QRType {
  a4v = "a4v",
  a4h = "a4h",
  sticker = "sticker",
  card_back = "card_back",
  card_front = "card_front",
}

const QRTypes: Record<QRType, string> = {
  a4v: "А4 Вертикальный",
  a4h: "А4 Горизонтальный",
  sticker: "Стикер",
  card_back: "Визитка (оборот)",
  card_front: "Визитка",
};

type QRVariant =
  | "blue"
  | "black_pink"
  | "pink"
  | "lightpink"
  | "black_red"
  | "space"
  | "blue_neon"
  | "microsoft";

const QRVariants: Record<QRVariant, string> = {
  blue: "Синий - VERTEX",
  black_pink: "Черный с розовым - VERTEX",
  pink: "Розовый - VERTEX",
  lightpink: "Ярко-розовый - VERTEX",
  black_red: "Черный с красным - Нейтральный",
  blue_neon: "Неоновый синий - Нейтральный",
  space: "Космос - Нейтральный",
  microsoft: "Майкрософт - Нейтральный",
};

const qrPositions: Record<
  QRType,
  { top: string; left: string; width: string }
> = {
  a4v: { top: "68.4%", left: "9.4%", width: "58%" },
  a4h: { top: "56%", left: "65.8%", width: "45%" },
  sticker: { top: "33.8%", left: "28.4%", width: "66%" },
  card_back: { top: "49%", left: "70.4%", width: "43%" },
  card_front: { top: "32.85%", left: "39.3%", width: "45%" },
};

const neutralQRVariantsStickerPosition = {
  top: "39.1%",
  left: "28.4%",
  width: "66%",
};

// создаем новый инстанс qr кода, чтобы избежать использование синглтона
const createQrInstance = () =>
  new QRCodeStyling({
    type: "canvas",
    width: 500,
    height: 500,
    dotsOptions: { color: "#000000", type: "extra-rounded" },
    backgroundOptions: { color: "#ffffff" },
    qrOptions: { errorCorrectionLevel: "Q" },
    cornersDotOptions: { type: "extra-rounded" },
    cornersSquareOptions: { type: "extra-rounded" },
  });

interface QRImagePreviewProps {
  variant: QRVariant;
  type: QRType;
  url: string;
}

const QRImagePreview = ({ variant, type, url }: QRImagePreviewProps) => {
  const qrRef = useRef<HTMLDivElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // здесь я использую стрелочную анонимную функцию для lazy инициализации
  const [qr] = useState(() => createQrInstance());

  // дублирование логики, убрать
  const getPosition = useCallback(() => {
    if (
      ["black_red", "blue_neon", "space", "microsoft"].includes(variant) &&
      type === QRType.sticker
    ) {
      return neutralQRVariantsStickerPosition;
    }
    return qrPositions[type];
  }, [variant, type]);

  useEffect(() => {
    setImageLoaded(false);
  }, [variant, type]);

  // обновил эффект чтобы не было ошибок при открытии и закрытии модалки (append синглтона - место для бага)
  // так как до этого у тебя ref ссылылся на пустоту после повторного открытия модалки
  useEffect(() => {
    const container = qrRef.current;

    if (!container) return;

    container.innerHTML = "";
    qr.append(container);

    return () => {
      container.innerHTML = "";
    };
  }, [qr]);

  useEffect(() => {
    const container = qrRef.current;

    if (!container) return;

    qr.update({ data: url });

    const canvas = qrRef.current.getElementsByTagName("canvas")[0];

    if (canvas) {
      const width = getPosition().width;
      canvas.style.width = width;
      canvas.style.height = "auto";
    }
  }, [url, type, getPosition, qr]);

  return (
    <div style={{ position: "relative", background: "transparent" }}>
      {!imageLoaded && <Skeleton className="h-[380px] w-[550px]" />}
      <img
        style={{ display: !imageLoaded ? "none" : "block" }}
        className="rounded-md" // Скругление только для превью
        onLoad={() => setImageLoaded(true)}
        // Используем WebP для быстрой загрузки
        src={`/img/qr/${variant}/${type}.webp`}
        alt="QR Code Preview"
      />
      <div
        ref={qrRef}
        style={{
          display: !imageLoaded ? "none" : "block",
          position: "absolute",
          objectFit: "contain",
          ...getPosition(),
        }}
      />
    </div>
  );
};

interface QRGeneratorModalProps {
  linkName: string;
  link: string;
  open: boolean;
  onClose: () => void;
  target: LeadLinkTarget;
}

export const QRGeneratorModal = ({
  open,
  onClose,
  link,
  linkName,
  target,
}: QRGeneratorModalProps) => {
  const [type, setType] = useState<QRType>(QRType.a4h);
  const [variant, setVariant] = useState<QRVariant>("blue");
  const [isDownloading, setIsDownloading] = useState(false);
  const [qr] = useState(() => createQrInstance());
  const { toast } = useToast();

  const availableVariants = Object.entries(QRVariants).filter(([key]) => {
    if (target === "bot-survey" || target === "bot-public") {
      return ["blue", "black_pink", "pink", "lightpink"].includes(
        key as QRVariant,
      );
    }
    return true;
  });

  // дублирование логики, убрать
  const getPosition = useCallback(
    (currentVariant: QRVariant, currentType: QRType) => {
      if (
        ["black_red", "blue_neon", "space", "microsoft"].includes(
          currentVariant,
        ) &&
        currentType === QRType.sticker
      ) {
        return neutralQRVariantsStickerPosition;
      }
      return qrPositions[currentType];
    },
    [],
  );

  const exportQR = useCallback(async () => {
    if (!link || link.includes("undefined")) {
      toast({ title: "Ошибка", description: "Ссылка некорректна" });
      return;
    }

    setIsDownloading(true);
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Не удалось получить 2D контекст canvas");

      // Загрузка фона
      const backgroundImg = await new Promise<HTMLImageElement>(
        (resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = `/img/qr/${variant}/${type}.png`;
        },
      );

      const { naturalWidth, naturalHeight } = backgroundImg;

      // Отрисовка фона
      canvas.width = naturalWidth;
      canvas.height = naturalHeight;
      ctx.drawImage(backgroundImg, 0, 0, naturalWidth, naturalHeight);

      // Получение данных куара
      qr.update({ data: link });
      const blob = await qr.getRawData("png");
      if (!blob) throw new Error("Не удалось получить данные QR-кода");

      const qrCodeUrl = URL.createObjectURL(blob as Blob);
      const qrCodeImage = await new Promise<HTMLImageElement>(
        (resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = qrCodeUrl;
        },
      );

      // РАСЧЕТ РАЗМЕРА И ПОЗИЦИИ
      const position = getPosition(variant, type);
      const areaWidth = naturalWidth * (parseFloat(position.width) / 100);
      const areaTop = naturalHeight * (parseFloat(position.top) / 100);
      const areaLeft = naturalWidth * (parseFloat(position.left) / 100);
      const widthPercentage = parseFloat(position.width);

      // Рассчитываем делитель для корректировки куара
      const divisor = 100 / widthPercentage;
      const qrDrawWidth = areaWidth / divisor;

      ctx.drawImage(qrCodeImage, areaLeft, areaTop, qrDrawWidth, qrDrawWidth);
      URL.revokeObjectURL(qrCodeUrl);

      // Экспорт
      const dataUrl = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `${linkName}-${variant}-${type}.png`;
      downloadLink.href = dataUrl;
      downloadLink.click();
    } catch (error) {
      console.error("Не удалось сгенерировать QR-код:", error);
    } finally {
      setIsDownloading(false);
    }
  }, [variant, type, link, linkName, getPosition, qr, toast]);

  return (
    <Modal open={open} onOpenChange={onClose}>
      <Modal.Content className="overflow-x-hidden overflow-y-hidden sm:overflow-y-auto md:min-w-[600px]">
        <Modal.Title>QR-Код</Modal.Title>
        <QRImagePreview variant={variant} type={type} url={link} />

        <div className="mt-1 flex flex-col space-y-5">
          <div className="flex gap-4">
            <Select
              value={type}
              onValueChange={(type) => setType(type as QRType)}
            >
              <SelectTrigger className="w-full text-textColor">
                <SelectValue placeholder="Размер" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(QRTypes).map(([key, value]) => (
                  <SelectItem key={key} value={key}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={variant}
              onValueChange={(variant) => setVariant(variant as QRVariant)}
            >
              <SelectTrigger className="w-full text-textColor">
                <SelectValue placeholder="Вариант" />
              </SelectTrigger>
              <SelectContent>
                {availableVariants.map(([key, value]) => (
                  <SelectItem key={key} value={key}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={exportQR}
            disabled={isDownloading}
            className="mt-4 h-[40px]"
          >
            {isDownloading ? <Spinner /> : "Скачать"}
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  );
};
